import { useEffect, useState } from "react";

import {
  getLeadCaptureLink,
  updateLeadCaptureLink,
} from "../../../../services/api-clinic";
import {
  buildLeadCaptureShareUrl,
  getLeadCaptureSlugValidationError,
  normalizeLeadCaptureSlug,
} from "../../../../utils/leadCaptureLinks";
import type { TSession } from "../../../../utils/types";

const UNAVAILABLE_SHARE_LINK = "Lead capture link unavailable";

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

type UseLeadCaptureShareLinkParams = {
  clinicSdkKey?: string;
  session?: TSession | null;
};

export const useLeadCaptureShareLink = ({
  clinicSdkKey,
  session,
}: UseLeadCaptureShareLinkParams) => {
  const [shareLink, setShareLink] = useState<string>(
    "Generating share link...",
  );
  const [slugInput, setSlugInput] = useState<string>("");
  const [slugError, setSlugError] = useState<string>("");
  const [slugMessage, setSlugMessage] = useState<string>("");
  const [isLeadCaptureLinkLoading, setIsLeadCaptureLinkLoading] =
    useState<boolean>(false);
  const [isLeadCaptureLinkSaving, setIsLeadCaptureLinkSaving] =
    useState<boolean>(false);
  const [savedLeadCaptureSlug, setSavedLeadCaptureSlug] = useState<string>("");

  const tierOneShareLink = buildLeadCaptureShareUrl({
    clinicSdkKey: session?.clinicSdkKeys?.[0] || clinicSdkKey,
  });
  const isShareLinkReady = shareLink.startsWith("http");

  useEffect(() => {
    if (!tierOneShareLink || !session?.clinicId) {
      setShareLink(tierOneShareLink || UNAVAILABLE_SHARE_LINK);
      setSavedLeadCaptureSlug("");
      setSlugInput("");
      setSlugError(
        tierOneShareLink
          ? ""
          : "Lead capture link is unavailable because no clinic SDK key is available.",
      );
      setSlugMessage("");
      return;
    }

    const abortController = new AbortController();

    setIsLeadCaptureLinkLoading(true);
    setShareLink("Generating share link...");
    setSlugError("");
    setSlugMessage("");

    getLeadCaptureLink(session, { signal: abortController.signal })
      .then((leadCaptureLink) => {
        if (abortController.signal.aborted) return;

        const nextSlug = leadCaptureLink.slug || "";
        setSavedLeadCaptureSlug(nextSlug);
        setSlugInput(nextSlug);
        setShareLink(
          leadCaptureLink.url ||
            buildLeadCaptureShareUrl({ slug: nextSlug }) ||
            tierOneShareLink ||
            UNAVAILABLE_SHARE_LINK,
        );
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;

        setSavedLeadCaptureSlug("");
        setSlugInput("");
        setShareLink(tierOneShareLink || UNAVAILABLE_SHARE_LINK);
        setSlugError(
          tierOneShareLink
            ? "Custom slug is unavailable right now. The standard form link is ready to copy."
            : getErrorMessage(error, "Lead capture link is unavailable."),
        );
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsLeadCaptureLinkLoading(false);
        }
      });

    return () => abortController.abort();
  }, [session, tierOneShareLink]);

  const normalizedSlugInput = normalizeLeadCaptureSlug(slugInput);
  const isSlugDirty = normalizedSlugInput !== savedLeadCaptureSlug;

  const saveSlug = async (): Promise<string | null> => {
    if (!session?.clinicId || !tierOneShareLink || isLeadCaptureLinkSaving) {
      return null;
    }

    const nextSlug = normalizedSlugInput;
    setSlugInput(nextSlug);

    const validationError = getLeadCaptureSlugValidationError(nextSlug);
    if (validationError) {
      setSlugError(validationError);
      setSlugMessage("");
      return null;
    }

    if (nextSlug === savedLeadCaptureSlug) {
      setSlugError("");
      setSlugMessage("Custom slug is already saved.");
      return shareLink;
    }

    setIsLeadCaptureLinkSaving(true);
    setSlugError("");
    setSlugMessage("");

    try {
      const leadCaptureLink = await updateLeadCaptureLink(session, nextSlug);
      const savedSlug = leadCaptureLink.slug || nextSlug;
      setSavedLeadCaptureSlug(savedSlug);
      setSlugInput(savedSlug);
      const nextShareLink =
        leadCaptureLink.url ||
        buildLeadCaptureShareUrl({ slug: savedSlug }) ||
        tierOneShareLink ||
        UNAVAILABLE_SHARE_LINK;
      setShareLink(nextShareLink);
      setSlugMessage("Custom slug saved.");
      return nextShareLink;
    } catch (error) {
      setSlugError(getErrorMessage(error, "Unable to save this custom slug."));
      return null;
    } finally {
      setIsLeadCaptureLinkSaving(false);
    }
  };

  const normalizeSlugInput = () => {
    setSlugInput((value) => normalizeLeadCaptureSlug(value));
  };

  const clearSlugFeedback = () => {
    setSlugError("");
    setSlugMessage("");
  };

  const resetSlugInput = () => {
    setSlugInput(savedLeadCaptureSlug);
    setSlugError("");
    setSlugMessage("");
  };

  return {
    clearSlugFeedback,
    isLeadCaptureLinkLoading,
    isLeadCaptureLinkSaving,
    isSlugDirty,
    isShareLinkReady,
    normalizeSlugInput,
    resetSlugInput,
    saveSlug,
    setSlugInput,
    shareLink,
    slugError,
    slugInput,
    slugMessage,
  };
};
